import { useState } from "react";
import { useAuth } from "../../lib/auth";
import { useIntl } from "../../lib/i18n";
import { api } from "../../lib/api";
import { useFetch } from "../../lib/useFetch";

// Per-tenant AI activation panel (epic 28.4). OWNER edits enabled, daily cap,
// Anthropic API key; everyone with /admin/* access sees the read-only
// status (plan tier, today/month spend, breaker state). Roles are
// enforced server-side — the PUT/POST return 403 for non-OWNER.
type Status = {
  planTier: "free" | "standard" | "pro";
  enabled: boolean;
  hasKey: boolean;
  dailyCapMicros: number;
  todaySpendMicros: number;
  monthSpendMicros: number;
  breakerOpen: boolean;
  breakerOpensAt?: string;
  lastProviderError?: string;
  lastProviderErrorAt?: string;
  upgradeRequired: boolean;
};

function usd(micros: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(micros / 1_000_000);
}

export default function AIActivationPage() {
  const { t } = useIntl();
  const { session } = useAuth();
  const { data, loading, error, refresh } = useFetch<Status>(
    "/api/v1/tenant/ai/activation",
  );
  const [keyInput, setKeyInput] = useState("");
  const [capInput, setCapInput] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const saveCap = async () => {
    setActionError(null);
    setSubmitting(true);
    try {
      const cap = Number(capInput) * 1_000_000;
      if (!Number.isFinite(cap) || cap < 0) {
        throw new Error("Cap must be a non-negative number of USD");
      }
      const r = await api("/api/v1/tenant/ai/activation", {
        method: "PUT",
        token: session?.token,
        body: JSON.stringify({ dailyCapMicros: Math.round(cap) }),
      });
      if (!r.ok) throw new Error(await r.text());
      setCapInput("");
      refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleEnabled = async () => {
    setActionError(null);
    setSubmitting(true);
    try {
      const r = await api("/api/v1/tenant/ai/activation", {
        method: "PUT",
        token: session?.token,
        body: JSON.stringify({ enabled: !data?.enabled }),
      });
      if (!r.ok) throw new Error(await r.text());
      refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const saveKey = async () => {
    setActionError(null);
    setSubmitting(true);
    try {
      const r = await api("/api/v1/tenant/ai/key", {
        method: "POST",
        token: session?.token,
        body: JSON.stringify({ key: keyInput }),
      });
      if (!r.ok) throw new Error(await r.text());
      setKeyInput("");
      refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteKey = async () => {
    setActionError(null);
    setSubmitting(true);
    try {
      const r = await api("/api/v1/tenant/ai/key", {
        method: "DELETE",
        token: session?.token,
      });
      if (!r.ok) throw new Error(await r.text());
      refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-brand-900">{t("ai.activation.title")}</h1>
      <p className="mt-2 text-sm text-slate-500">{t("ai.activation.subtitle")}</p>

      {loading && <p className="mt-4 text-slate-500">…</p>}
      {error && <p className="mt-4 text-rose-600">{error}</p>}
      {actionError && <p className="mt-4 text-rose-600">{actionError}</p>}

      {data && (
        <>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <Card label={t("ai.activation.plan_tier")} value={data.planTier} />
            <Card
              label={t("ai.activation.status")}
              value={data.enabled ? t("ai.activation.status.enabled") : t("ai.activation.status.disabled")}
              tone={data.enabled ? "ok" : "warn"}
            />
            <Card
              label={t("ai.activation.has_key")}
              value={data.hasKey ? t("ai.activation.has_key.yes") : t("ai.activation.has_key.no")}
              tone={data.hasKey ? "ok" : "warn"}
            />
            <Card label={t("ai.activation.today_spend")} value={usd(data.todaySpendMicros)} />
            <Card label={t("ai.activation.month_spend")} value={usd(data.monthSpendMicros)} />
            <Card label={t("ai.activation.daily_cap")} value={usd(data.dailyCapMicros)} />
            <Card
              label={t("ai.activation.breaker")}
              value={data.breakerOpen ? t("ai.activation.breaker.open") : t("ai.activation.breaker.closed")}
              tone={data.breakerOpen ? "err" : "ok"}
            />
            <Card
              label={t("ai.activation.last_error")}
              value={data.lastProviderError ?? "—"}
              tone={data.lastProviderError ? "err" : "ok"}
            />
            <Card
              label={t("ai.activation.last_error_at")}
              value={data.lastProviderErrorAt ?? "—"}
            />
          </div>

          {data.upgradeRequired && (
            <div className="mt-6 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm">
              <strong>{t("ai.activation.upgrade.title")}.</strong> {t("ai.activation.upgrade.body")}
            </div>
          )}

          <h2 className="mt-10 text-lg font-semibold text-brand-900">
            {t("ai.activation.controls")}
          </h2>
          <div className="mt-4 grid gap-6 md:grid-cols-2">
            <section className="rounded-md bg-white p-4 ring-1 ring-slate-200">
              <h3 className="text-sm font-medium text-slate-700">
                {t("ai.activation.enabled.title")}
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                {t("ai.activation.enabled.help")}
              </p>
              <button
                type="button"
                onClick={toggleEnabled}
                disabled={submitting}
                className="mt-3 rounded-md bg-brand-700 px-3 py-2 text-sm text-white hover:bg-brand-500 disabled:opacity-50"
              >
                {data.enabled
                  ? t("ai.activation.action.disable")
                  : t("ai.activation.action.enable")}
              </button>
            </section>

            <section className="rounded-md bg-white p-4 ring-1 ring-slate-200">
              <h3 className="text-sm font-medium text-slate-700">
                {t("ai.activation.cap.title")}
              </h3>
              <p className="mt-1 text-xs text-slate-500">{t("ai.activation.cap.help")}</p>
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={capInput}
                  onChange={(e) => setCapInput(e.target.value)}
                  placeholder={(data.dailyCapMicros / 1_000_000).toString()}
                  className="w-32 rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={saveCap}
                  disabled={submitting || !capInput}
                  className="rounded-md bg-brand-700 px-3 py-2 text-sm text-white hover:bg-brand-500 disabled:opacity-50"
                >
                  {t("common.save")}
                </button>
              </div>
            </section>

            <section className="rounded-md bg-white p-4 ring-1 ring-slate-200 md:col-span-2">
              <h3 className="text-sm font-medium text-slate-700">
                {t("ai.activation.key.title")}
              </h3>
              <p className="mt-1 text-xs text-slate-500">{t("ai.activation.key.help")}</p>
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="password"
                  autoComplete="off"
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                  placeholder="sk-ant-…"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs"
                />
                <button
                  type="button"
                  onClick={saveKey}
                  disabled={submitting || !keyInput}
                  className="rounded-md bg-brand-700 px-3 py-2 text-sm text-white hover:bg-brand-500 disabled:opacity-50"
                >
                  {t("ai.activation.key.save")}
                </button>
                {data.hasKey && (
                  <button
                    type="button"
                    onClick={deleteKey}
                    disabled={submitting}
                    className="rounded-md border border-rose-300 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                  >
                    {t("ai.activation.key.delete")}
                  </button>
                )}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function Card({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "ok" | "warn" | "err";
}) {
  const ring =
    tone === "ok"
      ? "ring-emerald-300"
      : tone === "warn"
        ? "ring-amber-300"
        : tone === "err"
          ? "ring-rose-300"
          : "ring-slate-200";
  return (
    <div className={`rounded-md bg-white p-4 ring-1 ${ring}`}>
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold text-brand-900">{value}</div>
    </div>
  );
}
